import { useState } from "react";
import { useKV } from "@/hooks/use-kv-adapter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Asset } from "@/types/core-hr";
import { Desktop, MagnifyingGlass, Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export function AssetRegistry() {
  const [assets, setAssets] = useKV<Asset[]>("core-hr-assets", []);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [formData, setFormData] = useState<Partial<Asset>>({});

  const handleSave = () => {
    if (!formData.name || !formData.type || !formData.serialNumber) {
      toast.error("Please fill in required fields");
      return;
    }

    if (editingAsset) {
      const updated = assets.map(a => a.id === editingAsset.id ? { ...a, ...formData } as Asset : a);
      setAssets(updated);
      toast.success("Asset updated");
    } else {
      const newAsset: Asset = {
        id: crypto.randomUUID(),
        name: formData.name,
        type: formData.type as any,
        serialNumber: formData.serialNumber,
        status: 'Available',
        condition: 'New',
        ...formData
      } as Asset;
      setAssets([newAsset, ...assets]);
      toast.success("Asset added");
    }
    setIsDialogOpen(false);
    setEditingAsset(null);
    setFormData({});
  };

  const handleDelete = (id: string) => {
    if (confirm("Are you sure?")) {
      setAssets(assets.filter(a => a.id !== id));
      toast.success("Asset deleted");
    }
  };

  const filteredAssets = assets.filter(asset => 
    asset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    asset.serialNumber.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="relative w-64">
          <MagnifyingGlass className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
          <Input 
            placeholder="Search assets..." 
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Button onClick={() => { setEditingAsset(null); setFormData({}); setIsDialogOpen(true); }}>
          <Plus className="mr-2 h-4 w-4" /> Add Asset
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle>Asset Registry</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Asset Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Serial No</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Assigned To</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAssets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    No assets found.
                  </TableCell>
                </TableRow>
              ) : (
                filteredAssets.map((asset) => (
                  <TableRow key={asset.id}>
                    <TableCell className="font-medium flex items-center gap-2">
                      <Desktop className="text-blue-500" /> {asset.name}
                    </TableCell>
                    <TableCell>{asset.type}</TableCell>
                    <TableCell>{asset.serialNumber}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        asset.status === 'Available' ? 'bg-green-100 text-green-700' :
                        asset.status === 'Assigned' ? 'bg-blue-100 text-blue-700' : 
                        'bg-red-100 text-red-700'
                      }>
                        {asset.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{asset.assignedTo || '-'}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => { setEditingAsset(asset); setFormData(asset); setIsDialogOpen(true); }}>
                        <PencilSimple className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(asset.id)}>
                        <Trash className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingAsset ? 'Edit Asset' : 'Add New Asset'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Asset Name</Label>
              <Input 
                value={formData.name || ''} 
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g. MacBook Pro M1" 
              />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select 
                value={formData.type} 
                onValueChange={(v) => setFormData({...formData, type: v as any})}
              >
                <SelectTrigger><SelectValue placeholder="Select Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Laptop">Laptop</SelectItem>
                  <SelectItem value="Phone">Phone</SelectItem>
                  <SelectItem value="Monitor">Monitor</SelectItem>
                  <SelectItem value="Access Card">Access Card</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Serial Number</Label>
              <Input 
                value={formData.serialNumber || ''} 
                onChange={(e) => setFormData({...formData, serialNumber: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select 
                value={formData.status} 
                onValueChange={(v) => setFormData({...formData, status: v as any})}
              >
                <SelectTrigger><SelectValue placeholder="Select Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Available">Available</SelectItem>
                  <SelectItem value="Assigned">Assigned</SelectItem>
                  <SelectItem value="Maintenance">Maintenance</SelectItem>
                  <SelectItem value="Retired">Retired</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave}>Save Asset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
